import { defineStore } from 'pinia'
import { apiClient, type ApiError } from '@/api/client'

interface LoginResponse {
  username: string
  expires_at: string
}

interface AuthState {
  username: string | null
  expiresAtIso: string | null
  isLoading: boolean
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    username: null,
    expiresAtIso: null,
    isLoading: false,
  }),
  getters: {
    isAuthenticated: (state): boolean => state.username !== null,
    expiresAt: (state): Date | null =>
      state.expiresAtIso ? new Date(state.expiresAtIso) : null,
  },
  actions: {
    async login(username: string, password: string): Promise<void> {
      this.isLoading = true
      try {
        const res = await apiClient.post<LoginResponse>('/v1/auth/login', {
          username,
          password,
        })
        this.username = res.data.username
        this.expiresAtIso = res.data.expires_at
      } catch (err) {
        this.username = null
        this.expiresAtIso = null
        const apiErr = err as ApiError
        if (apiErr.response?.status === 401) {
          throw new Error('invalid_credentials')
        }
        if (!apiErr.response) {
          throw new Error('network')
        }
        throw new Error('unknown')
      } finally {
        this.isLoading = false
      }
    },

    async logout(): Promise<void> {
      try {
        await apiClient.post('/v1/auth/logout')
      } catch {
        // Even if backend call fails (e.g. session already gone), we wipe local state.
      } finally {
        this.username = null
        this.expiresAtIso = null
      }
    },

    async fetchMe(): Promise<void> {
      try {
        const res = await apiClient.get<LoginResponse>('/v1/auth/me')
        this.username = res.data.username
        this.expiresAtIso = res.data.expires_at
      } catch {
        this.username = null
        this.expiresAtIso = null
      }
    },
  },
  persist: {
    key: 'magnum-cloud-admin-auth',
    storage: localStorage,
    pick: ['username', 'expiresAtIso'],
  },
})
